import { Factory } from "@/data/dao/Factory";
import { Misc } from "@/data/util/Misc";

import { logError } from "@/shared/logging/errors";

import { CookieCache } from "./CookieCache";
import { HTML5LocalCache } from "./Html5LocalCache";
import type { IOfflineStorage } from "./OfflineStorage";

export class FallbackCookieCache implements IOfflineStorage {
    _localStorageCache: HTML5LocalCache;
    _cookiesCache: CookieCache;

    constructor() {
        this._localStorageCache = Factory.getInstance<HTML5LocalCache>(HTML5LocalCache);
        this._cookiesCache = Factory.getInstance<CookieCache>(CookieCache);
    }

    public read(key: string, useSerializer?: boolean): any {
        try {
            let value = this._cookiesCache.read(key, useSerializer);

            // if no value is found, try to read from local storage
            if (value == null && this.isSupportinHTML5LocalStorage()) {
                value = this._localStorageCache.read(key);
                this.write(key, value);
            }

            return value;
        } catch (ex) {
            logError(ex);
            return null;
        }
    }

    public write(key: string, value: any): void {
        this._cookiesCache.write(key, value);
        this._localStorageCache.write(key, value);
    }

    public readString(key: string): string | null {
        let value = this._cookiesCache.readString(key);
        // todo: fallback mechanism
        if (value && value.startsWith("{") && value.endsWith("}")) {
            value = this._cookiesCache.read(key);
            if (value) this.writeString(key, value);
        }
        return value;
    }

    public writeString(key: string, value: string): void {
        this._cookiesCache.writeString(key, value);
        this._localStorageCache.writeString(key, value);
    }

    public remove(key: string): void {
        this._cookiesCache.remove(key);
        this._localStorageCache.remove(key);
    }

    public static getClassName(): string {
        return "FallbackCookieCache";
    }

    private isSupportinHTML5LocalStorage(): boolean {
        let html5offlineCacheSupporting: boolean | null = null;
        if (html5offlineCacheSupporting == null) {
            if (typeof Storage !== "undefined") {
                html5offlineCacheSupporting = !Misc.privateSurfing;
            } else {
                html5offlineCacheSupporting = false;
            }
        }
        return html5offlineCacheSupporting;
    }
}
